import { apiClient, resolveSignedUrl } from '@/api/client'

/**
 * ZIPストリームのデータ型
 */
export type ZipStreamData = {
  type: 'image' | 'complete' | 'error'
  index?: number
  filename?: string
  mimeType?: string
  data?: string
  total?: number
  message?: string
}

// ============================================================
// 署名付きURL API
// ============================================================

/**
 * 署名付きURLレスポンス型
 */
export type SignedUrlResponse = {
  url: string
}

/**
 * バッチ署名付きURLレスポンス型
 */
export type SignedUrlsBatchResponse = {
  urls: Record<string, string>
}

/**
 * 署名対象のパスと、署名に含めない追加クエリを分離する
 *
 * サーバー側の署名はパス部分のみを対象とし、生成結果に `?expires=...&sig=...` を付与する。
 * `/e/1/stream?quality=480p` のようにクエリ付きで渡すと `?` が二重になり署名検証に失敗するため、
 * 追加クエリは署名後に `&` で連結する。
 */
const splitSignTarget = (path: string): [basePath: string, extraQuery: string] => {
  const index = path.indexOf('?')
  if (index === -1) return [path, '']
  return [path.slice(0, index), path.slice(index + 1)]
}

/**
 * 単一のパスに対して署名付きURLを取得
 * @param path メディアのパス（例: /products/monsnode/123/thumbnail）。クエリ付きも可
 * @returns 署名付きURL
 */
export const getSignedUrl = async (path: string): Promise<string> => {
  const [basePath, extraQuery] = splitSignTarget(path)
  const res = await apiClient.post<SignedUrlResponse>('/signed/url', { path: basePath })
  const signed = resolveSignedUrl(res.data.url)
  return extraQuery ? `${signed}&${extraQuery}` : signed
}

/**
 * 複数のパスに対して署名付きURLをバッチ取得
 * @param paths メディアパスの配列
 * @returns パスをキー、署名付きURLを値とするオブジェクト
 */
export const getSignedUrls = async (paths: string[]): Promise<Record<string, string>> => {
  if (paths.length === 0) {
    return {}
  }
  const res = await apiClient.post<SignedUrlsBatchResponse>('/signed/urls', { paths })
  // 各URLを絶対URLに変換
  const resolved: Record<string, string> = {}
  for (const [key, value] of Object.entries(res.data.urls)) {
    resolved[key] = resolveSignedUrl(value)
  }
  return resolved
}

/** pdf.js の scale=1 の viewport と同じ単位 (pt) のページサイズ */
export type PdfPageSize = {
  width: number
  height: number
}

type PdfPageSizesResponse = {
  episode_id: number
  sizes: PdfPageSize[]
}

/** PDF の全ページサイズを取得（ページ順） */
export const getPdfPageSizes = async (episodeId: number): Promise<PdfPageSize[]> => {
  const res = await apiClient.get<PdfPageSizesResponse>(`/e/${episodeId}/pdf/page-sizes`)
  return res.data.sizes
}

/**
 * 認証付きでZIPストリームを取得
 * @param url ストリームのURL
 * @param onData データ受信時のコールバック
 * @param signal AbortSignal
 * @param startIndex 開始インデックス（この位置から取得を開始）
 */
export const fetchZipStream = async (
  url: string,
  onData: (data: ZipStreamData) => void,
  signal?: AbortSignal,
  startIndex?: number,
): Promise<void> => {
  // URLが/api/で始まる場合、baseURLと重複しないよう調整
  let path = url.startsWith('/api/') ? url.slice(4) : url

  // startIndexをクエリパラメータとして追加
  if (startIndex !== undefined && startIndex > 0) {
    const separator = path.includes('?') ? '&' : '?'
    path = `${path}${separator}start_index=${startIndex}`
  }

  let processedLength = 0
  let buffer = ''

  await apiClient.get(path, {
    responseType: 'text',
    signal,
    headers: {
      Accept: 'text/plain',
    },
    onDownloadProgress: (progressEvent) => {
      const responseText = progressEvent.event?.target?.response as string | undefined
      if (!responseText) return

      // 新しく受信した部分のみを処理
      const newData = responseText.slice(processedLength)
      processedLength = responseText.length

      buffer += newData

      // --NEXT--区切りで分割して各画像データを処理
      const chunks = buffer.split('--NEXT--\n')
      buffer = chunks.pop() || ''

      for (const chunk of chunks) {
        const trimmedChunk = chunk.trim()
        if (trimmedChunk) {
          // 新形式を解析: "index:filename\nbase64data"
          const lines = trimmedChunk.split('\n')
          if (lines.length >= 2) {
            const firstLine = lines[0].trim()
            const base64Data = lines.slice(1).join('').trim()

            // index:filename 形式をパース
            let imageIndex: number
            let filename: string
            const colonIndex = firstLine.indexOf(':')
            if (colonIndex !== -1) {
              imageIndex = parseInt(firstLine.substring(0, colonIndex), 10)
              filename = firstLine.substring(colonIndex + 1)
            } else {
              // 後方互換性: インデックスがない場合はファイル名のみ
              imageIndex = 0
              filename = firstLine
            }

            if (filename && base64Data) {
              // ファイル名から画像形式を推測
              let mimeType = 'image/jpeg'
              const lowerFilename = filename.toLowerCase()
              if (lowerFilename.endsWith('.png')) {
                mimeType = 'image/png'
              } else if (lowerFilename.endsWith('.webp')) {
                mimeType = 'image/webp'
              } else if (lowerFilename.endsWith('.gif')) {
                mimeType = 'image/gif'
              } else if (lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg')) {
                mimeType = 'image/jpeg'
              }

              onData({
                type: 'image',
                index: imageIndex,
                filename,
                mimeType,
                data: base64Data,
              })
            }
          } else {
            // JSON形式のフォールバック
            try {
              const data = JSON.parse(trimmedChunk)
              if (data.type === 'image' || data.type === 'complete' || data.type === 'error') {
                onData(data)
              }
            } catch {
              // パースエラーは無視
            }
          }
        }
      }
    },
  })

  // 残ったバッファも処理
  const finalTrimmedBuffer = buffer.trim()
  if (finalTrimmedBuffer) {
    const lines = finalTrimmedBuffer.split('\n')
    if (lines.length >= 2) {
      const firstLine = lines[0].trim()
      const base64Data = lines.slice(1).join('').trim()

      // index:filename 形式をパース
      let imageIndex: number
      let filename: string
      const colonIndex = firstLine.indexOf(':')
      if (colonIndex !== -1) {
        imageIndex = parseInt(firstLine.substring(0, colonIndex), 10)
        filename = firstLine.substring(colonIndex + 1)
      } else {
        imageIndex = 0
        filename = firstLine
      }

      if (filename && base64Data) {
        let mimeType = 'image/jpeg'
        const lowerFilename = filename.toLowerCase()
        if (lowerFilename.endsWith('.png')) {
          mimeType = 'image/png'
        } else if (lowerFilename.endsWith('.webp')) {
          mimeType = 'image/webp'
        } else if (lowerFilename.endsWith('.gif')) {
          mimeType = 'image/gif'
        } else if (lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg')) {
          mimeType = 'image/jpeg'
        }

        onData({
          type: 'image',
          index: imageIndex,
          filename,
          mimeType,
          data: base64Data,
        })
      }
    } else {
      try {
        const data = JSON.parse(finalTrimmedBuffer)
        if (data.type === 'complete' || data.type === 'error') {
          onData(data)
        }
      } catch {
        // パースエラーは無視
      }
    }
  }

  onData({ type: 'complete' })
}

// ============================================================
// 外部メディアダウンロード: 共通ユーティリティ
// ============================================================

/**
 * SSE ストリームダウンロード（共通）
 */
const streamExternalDownload = async (
  endpoint: string,
  params: Record<string, string>,
  onMessage: (data: unknown) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<void> => {
  let processedLength = 0

  try {
    await apiClient.get(endpoint, {
      params,
      responseType: 'text',
      signal,
      headers: {
        Accept: 'text/event-stream',
      },
      onDownloadProgress: (progressEvent) => {
        const responseText = progressEvent.event?.target?.response as string | undefined
        if (!responseText) return

        const newData = responseText.slice(processedLength)
        processedLength = responseText.length

        const lines = newData.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6)
              const data = JSON.parse(jsonStr)
              onMessage(data)
            } catch {
              // パースエラーは無視
            }
          }
        }
      },
    })
  } catch (error) {
    if (signal?.aborted) return
    onError(error instanceof Error ? error : new Error('Unknown error'))
  }
}

/**
 * メディアファイル取得（共通）
 */
export const fetchExternalFile = async (
  filePath: string,
  onProgress?: (progress: number, loaded: number, total: number) => void,
): Promise<Blob> => {
  const res = await apiClient.get(filePath, {
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress, progressEvent.loaded, progressEvent.total)
      }
    },
  })
  return res.data
}

/**
 * サムネイル取得（共通）
 */
export const fetchExternalThumbnail = async (thumbnailPath: string): Promise<Blob> => {
  const res = await apiClient.get(thumbnailPath, { responseType: 'blob' })
  return res.data
}

// === 外部ビデオダウンロード (薄いラッパー) ===

export const streamExternalVideoDownload = (
  url: string,
  quality: 'high' | 'medium' | 'low',
  onMessage: (data: unknown) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<void> => streamExternalDownload('/external-video/download', { url, quality }, onMessage, onError, signal)

// === 外部音楽ダウンロード ===

export const streamExternalMusicDownload = (
  url: string,
  onMessage: (data: unknown) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<void> => streamExternalDownload('/external-music/download', { url }, onMessage, onError, signal)

// ============================================================
// CBZ 個別ページ取得API（優先読み込み用）
// ============================================================

/**
 * CBZマニフェストの型
 */
export type CbzManifest = {
  total: number
  files: string[]
}

/**
 * CBZファイルのマニフェスト（メタデータ）を取得
 * ZIPを展開せずにセントラルディレクトリのみ読み取るため高速
 * @param episodeId エピソードID
 * @param signedUrl 署名付きURL（オプション）
 */
export const fetchCbzManifest = async (episodeId: number, signedUrl?: string): Promise<CbzManifest> => {
  const url = signedUrl || `/e/${episodeId}/cbz/manifest`
  const res = await apiClient.get<CbzManifest>(url)
  return res.data
}

/**
 * CBZファイルから指定ページの画像を取得
 * @param episodeId エピソードID
 * @param pageIndex 0ベースのページインデックス
 * @param signedUrl 署名付きURL（オプション）
 * @returns 画像のBlob
 */
export const fetchCbzPage = async (
  episodeId: number,
  pageIndex: number,
  signedUrl?: string,
  signal?: AbortSignal,
): Promise<Blob> => {
  // 署名付きURLが提供された場合、/api/で始まる場合はbaseURLと重複しないよう調整
  let url: string
  if (signedUrl) {
    url = signedUrl.startsWith('/api/') ? signedUrl.slice(4) : signedUrl
  } else {
    url = `/e/${episodeId}/cbz/page/${pageIndex}`
  }
  const res = await apiClient.get(url, { responseType: 'blob', signal })
  return res.data
}
